import React from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useTotalBalance } from "@hooks/useFinancialAccounts";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { AccountsView } from "@common/types/views/accountsView";
import { ExpandedState } from "@tanstack/react-table";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { FeatureAccess } from "@components/settings/FeatureAccess/FeatureAccess";
import { Amount } from "@design-system/Amount/Amount";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useOpenMultiEntityUpgrade } from "@components/addEntity/useOpenMultiEntityUpgrade";
import { useStartAddEntity } from "@components/addEntity/useStartAddEntity";
import { HStack } from "@design-system/HStack/HStack";
import { AddAccount } from "./AddAccount";
import { accountsView, accountsViewConfig } from "./views/accountsViewConfig";
import styles from "./AccountsPage.module.scss";

interface Props {
    readonly view: AccountsView;
    readonly onViewChange: (view: AccountsView) => void;
    readonly expanded: ExpandedState;
    readonly tableRef: React.RefObject<ViewTableHandle | null>;
}

export const AccountsHeader: React.FC<Props> = ({
    view,
    onViewChange,
    expanded,
    tableRef,
}) => {
    const startAddEntity = useStartAddEntity();
    const totalBalance = useTotalBalance();
    const canCreateEntities = useCanPerformWorkspaceAction(
        WorkspaceAction.ENTITY_CREATE,
    );
    const openMultiEntityUpgrade = useOpenMultiEntityUpgrade();

    return (
        <header className={styles.header}>
            <div className={styles.headerTotalBalance}>
                <span className="label">Total balance</span>
                <Amount
                    amount={totalBalance}
                    separateCurrencySign
                    showNegative
                    className={styles.amount}
                />
            </div>
            <HStack gap={12}>
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <ColumnSettings
                    view={view}
                    onViewChange={onViewChange}
                    viewConfig={accountsViewConfig}
                    defaultView={accountsView}
                />
                {canCreateEntities && (
                    <FeatureAccess
                        feature={PREMIUM_FEATURES.UNLIMITED_ENTITIES}
                        renderAllowed={() => (
                            <Button
                                variant="primary"
                                onClick={() => startAddEntity()}
                                data-testid="add-entity-btn"
                                size="md"
                            >
                                Add entity
                            </Button>
                        )}
                        renderBlocked={({ canManageBilling }) => (
                            <Tooltip content="Upgrade to add multiple entities to Kick">
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={
                                        canManageBilling
                                            ? openMultiEntityUpgrade
                                            : undefined
                                    }
                                    disabled={!canManageBilling}
                                    data-testid="add-entity-btn-upgrade"
                                >
                                    Add entity
                                </Button>
                            </Tooltip>
                        )}
                    />
                )}

                <AddAccount
                    btnText="Add account"
                    btnVariant="primary"
                    btnSize="md"
                />
            </HStack>
        </header>
    );
};
